import React, { useState } from 'react';
import { ShieldCheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const ConsentModal = ({ isOpen, onAccept, onDecline, familyMember, family }) => {
  const [hasRead, setHasRead] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (hasRead && agreedToTerms) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 bg-secondary-800/75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-secondary-200/50 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-700 px-6 py-4 flex-shrink-0">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-white mr-3" />
            <div>
              <h2 className="font-serif text-xl font-semibold text-white">
                Data Protection & Privacy Consent
              </h2>
              <p className="text-primary-100 text-sm">
                Required under the Protection of Personal Information Act (POPIA)
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-4">
            {/* Personal Welcome */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="text-sm text-primary-900">
                Welcome <strong>{familyMember?.firstName} {familyMember?.lastName}</strong>,
                you are about to complete the wealth evaluation for the <strong>{family?.name}</strong> family.
              </p>
            </div>

            {/* Purpose of Data Collection */}
            <div>
              <h3 className="font-semibold text-secondary-800 mb-2">Purpose of Data Collection</h3>
              <p className="text-sm text-secondary-600 mb-2">
                We will collect your responses to questions about:
              </p>
              <ul className="list-disc list-inside text-sm text-secondary-600 space-y-1 ml-4">
                <li>Financial goals and objectives</li>
                <li>Investment preferences and risk tolerance</li>
                <li>Estate planning considerations</li>
                <li>Family governance perspectives</li>
                <li>Wealth transfer preferences</li>
              </ul>
            </div>

            {/* How Data Will Be Used */}
            <div>
              <h3 className="font-semibold text-secondary-800 mb-2">How Your Information Will Be Used</h3>
              <p className="text-sm text-secondary-600">
                Your responses will be used <strong>exclusively</strong> for:
              </p>
              <ul className="list-disc list-inside text-sm text-secondary-600 space-y-1 ml-4">
                <li>Creating a comprehensive family wealth management plan</li>
                <li>Identifying areas of alignment and divergence within your family</li>
                <li>Providing personalized wealth coaching recommendations</li>
                <li>Generating reports for family discussions</li>
              </ul>
            </div>

            {/* Data Security */}
            <div>
              <h3 className="font-semibold text-secondary-800 mb-2">Data Security & Storage</h3>
              <ul className="list-disc list-inside text-sm text-secondary-600 space-y-1 ml-4">
                <li>Your data is encrypted during transmission and storage</li>
                <li>Access is restricted to authorized personnel only</li>
                <li>Data is stored on secure cloud servers with industry-standard protection</li>
                <li className="font-semibold text-primary-700">Your data will be permanently deleted when our coaching engagement ends</li>
              </ul>
            </div>

            {/* Data Retention Policy */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex">
                <ShieldCheckIcon className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-primary-900 mb-1">
                    Data Retention Commitment
                  </h4>
                  <p className="text-sm text-primary-800">
                    We will retain your information only for the duration of our wealth coaching relationship.
                    Once our professional engagement concludes, all your personal data and evaluation
                    responses will be permanently deleted from our systems. This ensures your information
                    is not kept longer than necessary.
                  </p>
                </div>
              </div>
            </div>

            {/* Cross-border Transfer Notice */}
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-accent-900 mb-1">
                    International Data Transfer
                  </h4>
                  <p className="text-sm text-accent-800">
                    Your data may be processed and stored on servers located outside of South Africa.
                    We ensure that appropriate safeguards are in place to protect your information
                    in accordance with POPIA requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* Your Rights */}
            <div>
              <h3 className="font-semibold text-secondary-800 mb-2">Your Rights</h3>
              <p className="text-sm text-secondary-600 mb-2">Under POPIA, you have the right to:</p>
              <ul className="list-disc list-inside text-sm text-secondary-600 space-y-1 ml-4">
                <li>Access your personal information</li>
                <li>Correct or update your information</li>
                <li>Request deletion of your information at any time</li>
                <li>Object to processing of your information</li>
                <li>Lodge a complaint with the Information Regulator</li>
              </ul>
              <p className="text-sm text-secondary-600 mt-2 font-medium">
                Note: Your data will be automatically deleted when our coaching relationship ends,
                ensuring compliance with data minimization principles.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-secondary-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-secondary-800 mb-1">
                Questions or Concerns?
              </h4>
              <p className="text-sm text-secondary-600">
                If you have any questions about how your data will be handled, please contact
                your wealth coach before proceeding.
              </p>
            </div>
          </div>
        </div>

        {/* Consent Checkboxes */}
        <div className="border-t border-secondary-200 px-6 py-4 bg-secondary-50 flex-shrink-0">
          <div className="space-y-3">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={hasRead}
                onChange={(e) => setHasRead(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-secondary-700">
                I confirm that I have read and understood the above information
              </span>
            </label>
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-secondary-700">
                I consent to the collection and processing of my personal information as described above
              </span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center px-6 py-4 bg-secondary-100 border-t border-secondary-200 flex-shrink-0">
          <button
            onClick={onDecline}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            Decline & Exit
          </button>
          <button
            onClick={handleAccept}
            disabled={!hasRead || !agreedToTerms}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              hasRead && agreedToTerms
                ? 'bg-primary-500 hover:bg-primary-600'
                : 'bg-secondary-300 cursor-not-allowed'
            }`}
          >
            Accept & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;
